import { type AdminAgentNoteDailyCountRead } from '@libraries/typescript/api_client';

export type HeatmapBin = 0 | 1 | 2 | 3 | 4;

/** Quantize a count into quartile bins relative to the window max. */
export function binForCount(total: number, maxTotal: number): HeatmapBin {
    if (total <= 0 || maxTotal <= 0) {
        return 0;
    }
    const ratio = total / maxTotal;
    if (ratio <= 0.25) {
        return 1;
    }
    if (ratio <= 0.5) {
        return 2;
    }
    if (ratio <= 0.75) {
        return 3;
    }
    return 4;
}

function isoDateKey(date: Date): string {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Inclusive list of date keys from window_start to the day before the
 * exclusive window_end. Date math runs on UTC-noon anchors so keys never
 * shift across the browser's timezone or DST.
 */
export function windowDateKeys(
    windowStartIso: string,
    windowEndIso: string,
): Array<string> {
    const start = new Date(`${windowStartIso.slice(0, 10)}T12:00:00Z`);
    // window_end is an exclusive midnight; the last day is the one before.
    const end = new Date(`${windowEndIso.slice(0, 10)}T12:00:00Z`);
    end.setUTCDate(end.getUTCDate() - 1);
    const keys: Array<string> = [];
    for (
        const cursor = new Date(start);
        cursor <= end;
        cursor.setUTCDate(cursor.getUTCDate() + 1)
    ) {
        keys.push(isoDateKey(cursor));
    }
    return keys;
}

/** Zero-filled per-day totals aligned to the window (for sparklines). */
export function zeroFilledDailySeries(
    dailyCounts: Array<AdminAgentNoteDailyCountRead>,
    windowStartIso: string,
    windowEndIso: string,
): Array<{ date: string; total: number; failed: number }> {
    const byDate = new Map(dailyCounts.map((entry) => [entry.date, entry]));
    return windowDateKeys(windowStartIso, windowEndIso).map((date) => ({
        date,
        total: byDate.get(date)?.total ?? 0,
        failed: byDate.get(date)?.failed ?? 0,
    }));
}

export type CalendarCell = {
    readonly date: string;
    readonly total: number;
    readonly failed: number;
    readonly bin: HeatmapBin;
    /** Grid slots before the window start (rendered empty). */
    readonly isPadding: boolean;
    /** Days after the window (rendered as faint placeholders). */
    readonly isFuture: boolean;
};

export type CalendarGrid = {
    /** Column-major: weeks[weekIndex][dayIndex], dayIndex 0 = Sunday. */
    readonly weeks: Array<Array<CalendarCell>>;
    /** Month label per week column ('' when the month has not changed). */
    readonly monthLabels: Array<string>;
    readonly maxTotal: number;
};

function dayOfWeek(dateKey: string): number {
    return new Date(`${dateKey}T12:00:00Z`).getUTCDay();
}

function monthLabel(dateKey: string): string {
    return new Date(`${dateKey}T12:00:00Z`).toLocaleDateString('en-US', {
        month: 'short',
        timeZone: 'UTC',
    });
}

/**
 * GitHub-style contribution calendar: weeks as columns, weekdays as rows,
 * zero-filled from sparse daily counts and quantized against the window max.
 */
export function buildCalendarGrid(
    dailyCounts: Array<AdminAgentNoteDailyCountRead>,
    windowStartIso: string,
    windowEndIso: string,
    // Extends the grid beyond the window (e.g. through year end) with
    // faint future placeholders so a year-to-date view spans the year.
    gridEndIso?: string,
): CalendarGrid {
    const series = zeroFilledDailySeries(
        dailyCounts,
        windowStartIso,
        windowEndIso,
    );
    const maxTotal = Math.max(0, ...series.map((entry) => entry.total));

    const cells: Array<CalendarCell> = [];
    const firstDay = series[0] ? dayOfWeek(series[0].date) : 0;
    for (let index = 0; index < firstDay; index += 1) {
        cells.push({
            date: '',
            total: 0,
            failed: 0,
            bin: 0,
            isPadding: true,
            isFuture: false,
        });
    }
    for (const entry of series) {
        cells.push({
            date: entry.date,
            total: entry.total,
            failed: entry.failed,
            bin: binForCount(entry.total, maxTotal),
            isPadding: false,
            isFuture: false,
        });
    }
    if (gridEndIso) {
        for (const date of windowDateKeys(windowEndIso, gridEndIso)) {
            cells.push({
                date,
                total: 0,
                failed: 0,
                bin: 0,
                isPadding: false,
                isFuture: true,
            });
        }
    }

    const weeks: Array<Array<CalendarCell>> = [];
    for (let index = 0; index < cells.length; index += 7) {
        weeks.push(cells.slice(index, index + 7));
    }

    const monthLabels: Array<string> = [];
    let lastMonth = '';
    for (const week of weeks) {
        const firstRealCell = week.find((cell) => !cell.isPadding);
        const month = firstRealCell ? monthLabel(firstRealCell.date) : '';
        if (month !== '' && month !== lastMonth) {
            monthLabels.push(month);
            lastMonth = month;
        } else {
            monthLabels.push('');
        }
    }

    return { weeks, monthLabels, maxTotal };
}
